import { openEntry, readEntryJson, readZipIndex, type ZipEntry } from "../zip/zip";
import { WALK_ROUTE, type PageToWorker, type WalkSession, type WorkerToPage } from "./protocol";
import { effectiveSearch, gpuMbOverride } from "./advanced";
import { assertOriginalsOrAstc } from "./compressed";
import { effectiveGpuMb, formatOverridesFromSearch, gpuEstimateMb, gpuUncompressedMb, probeAstcSupport } from "./memory";
import { getWalkBlob, getWalkSummary, putWalk, setWalkEstimate, touchLastOpened, type StoredWalk } from "./storage";

/** Newest .walk format this Player understands. Older ones keep opening. */
export const SUPPORTED_WALK_FORMAT = 1;

interface WalkJson {
  format?: string;
  format_version?: number;
  name?: string;
  /** Optional: estimated graphics memory in MB (see docs/WALK_FORMAT.md). */
  gpu_mb?: number;
  /** Optional: the estimate from the original files, with compressed.json (used on devices without ASTC HDR). */
  gpu_mb_uncompressed?: number;
  /** Optional: the Walk Bake export preset. */
  preset?: string;
}

export interface OpenedWalk {
  id: string;
  name: string;
  session: WalkSession;
  /** URL of the viewer page for this walk; the service worker serves the walk's files from the ZIP. */
  url: string;
  /** False when the walk could only be opened in-memory (storage quota exceeded, etc.). */
  stored: boolean;
  /** Opened from a URL (`?walk=`) on purpose not saved: no "could not save" notice. */
  temporary?: boolean;
  /** Estimated graphics memory in MB for what this Player loads (the originals), when the walk says. */
  gpuMb?: number;
  preset?: string;
}

/** Reads the ZIP directory (and walk.json if present) and finds the exported walk inside it. */
async function parseWalk(
  blob: Blob,
  fallbackName: string,
): Promise<{
  name: string;
  root: string;
  entries: ZipEntry[];
  gpuMb?: number;
  gpuMbUncompressed?: number;
  preset?: string;
}> {
  const index = await readZipIndex(blob);
  // scene.json at the root or one folder down (e.g. Windows "Compress to ZIP").
  // The walk carries data only: the viewer is the Player's own (src/pwa/sw.ts).
  const root = [...index.keys()]
    .filter((name) => name === "scene.json" || /^[^/]+\/scene\.json$/.test(name))
    .map((name) => name.slice(0, -"scene.json".length))
    .sort((a, b) => a.length - b.length)[0];
  if (root === undefined) {
    throw new Error("No Walk Bake scene in this file (scene.json is missing).");
  }

  // A walk without its original textures needs ASTC HDR: without it, say so before anything is saved.
  await assertOriginalsOrAstc(blob, index, root, probeAstcSupport());

  let name = fallbackName;
  let meta: WalkJson | undefined;
  const walkJson = index.get(`${root}walk.json`);
  if (walkJson) {
    meta = await readEntryJson<WalkJson>(blob, walkJson);
    if ((meta.format_version ?? 1) > SUPPORTED_WALK_FORMAT) {
      throw new Error("This walk was made with a newer Walk Bake. Update the Player to open it.");
    }
    if (meta.name) name = meta.name;
  }

  // Estimate: walk.json gpu_mb; older walks: export_report.json gpu.total_mb; otherwise none.
  let report: unknown;
  const reportEntry = index.get(`${root}export_report.json`);
  if (reportEntry && meta?.gpu_mb === undefined) {
    try {
      report = await readEntryJson<unknown>(blob, reportEntry);
    } catch {
      // The report is optional and only informs the estimate.
    }
  }
  const gpuMb = gpuEstimateMb(meta, report);
  const gpuMbUncompressed = gpuUncompressedMb(meta);
  const preset = typeof meta?.preset === "string" ? meta.preset : undefined;

  return { name, root, entries: [...index.values()] as ZipEntry[], gpuMb, gpuMbUncompressed, preset };
}

async function toOpenedWalk(
  id: string,
  blob: Blob,
  root: string,
  entries: ZipEntry[],
  name: string,
  stored: boolean,
  gpuMb?: number,
  gpuMbUncompressed?: number,
  preset?: string,
): Promise<OpenedWalk> {
  const session: WalkSession = { id, file: blob, root, entries };
  const scope = (await navigator.serviceWorker.ready).scope;
  return { id, name, session, url: `${scope}${WALK_ROUTE}${id}/index.html`, stored, gpuMb: gpuMbOverride(effectiveSearch()) ?? effectiveGpuMb(gpuMb, gpuMbUncompressed, probeAstcSupport(), formatOverridesFromSearch(effectiveSearch())), preset };
}

/**
 * Validates a freshly picked file and saves it to on-device storage so it
 * can be reopened later without the original file (offline, or if it was
 * deleted / is an iCloud-only placeholder). If saving fails (storage
 * quota, private browsing, ...) the walk still opens - just in-memory for
 * this session only - and `stored` comes back false so the UI can say so.
 */
export async function openPickedFile(file: File, options: { save?: boolean } = {}): Promise<OpenedWalk> {
  const id = crypto.randomUUID();
  const fallbackName = file.name.replace(/\.(walk|zip)$/i, "");
  const { name, root, entries, gpuMb, gpuMbUncompressed, preset } = await parseWalk(file, fallbackName);

  const save = options.save !== false;
  let stored = save;
  try {
    if (!save) throw new Error("not saved");
    const record: StoredWalk = {
      id,
      name,
      fileSize: file.size,
      importedAt: Date.now(),
      lastOpenedAt: Date.now(),
      blob: file,
      ...(gpuMb !== undefined && { gpuMb }),
      ...(gpuMbUncompressed !== undefined && { gpuMbUncompressed }),
      ...(preset !== undefined && { preset }),
    };
    await putWalk(record);
  } catch {
    stored = false;
  }

  const opened = await toOpenedWalk(id, file, root, entries, name, stored, gpuMb, gpuMbUncompressed, preset);
  return save ? opened : { ...opened, temporary: true };
}

/** A saved walk whose bytes cannot be read back from this device's storage. */
export class UnreadableWalkError extends Error {
  constructor(readonly walkId: string) {
    super("This saved walk can no longer be read on this device. Open the file again.");
    this.name = "UnreadableWalkError";
  }
}

/**
 * Re-opens a walk saved by openPickedFile. Re-parses the ZIP directory (cheap: a few KB) and
 * reads scene.json once, so a stored file the browser can no longer read fails here with a
 * clear error instead of as a raw 500 once the viewer is already loading.
 */
export async function openStoredWalk(id: string): Promise<OpenedWalk> {
  const [summary, blob] = await Promise.all([getWalkSummary(id), getWalkBlob(id)]);
  if (!summary || !blob) {
    throw new Error("This walk is no longer saved on this device. Open the file again.");
  }
  let parsed: Awaited<ReturnType<typeof parseWalk>>;
  try {
    parsed = await parseWalk(blob, summary.name);
    const scene = parsed.entries.find((entry) => entry.name === `${parsed.root}scene.json`);
    if (scene) await new Response(await openEntry(blob, scene)).arrayBuffer();
  } catch (err) {
    // A newer-format or damaged-but-readable ZIP keeps its own message; only read failures are "unreadable".
    if (err instanceof Error && /newer Walk Bake|scene\.json is missing|needs ASTC HDR/.test(err.message)) throw err;
    throw new UnreadableWalkError(id);
  }
  // Metadata only: the stored file is not rewritten, so the Blob handed out above stays valid.
  await touchLastOpened(id);
  // A walk saved by an older Player has no estimate on its card yet.
  // Same for a walk saved before gpu_mb_uncompressed was read: its card would show the compressed figure.
  if (
    (summary.gpuMb === undefined && parsed.gpuMb !== undefined) ||
    (summary.gpuMbUncompressed === undefined && parsed.gpuMbUncompressed !== undefined)
  ) {
    await setWalkEstimate(id, parsed.gpuMb, parsed.gpuMbUncompressed, parsed.preset).catch(() => undefined);
  }
  return toOpenedWalk(id, blob, parsed.root, parsed.entries, parsed.name, true, parsed.gpuMb, parsed.gpuMbUncompressed, parsed.preset);
}

let current: WalkSession | null = null;

async function postToWorker(message: PageToWorker): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const worker = registration.active;
  if (!worker) throw new Error("The Player is still installing. Wait a moment and try again.");
  await new Promise<void>((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => reject(new Error("The Player's offline service did not answer.")), 5000);
    channel.port1.onmessage = () => {
      clearTimeout(timer);
      resolve();
    };
    worker.postMessage(message, [channel.port2]);
  });
}

/** Hands the walk to the service worker so its viewer page can load from the ZIP. */
export async function startWalk(walk: OpenedWalk): Promise<void> {
  current = walk.session;
  await postToWorker({ type: "walk-open", session: walk.session });
}

export function stopWalk(): void {
  if (!current) return;
  const id = current.id;
  current = null;
  void postToWorker({ type: "walk-close", id }).catch(() => undefined);
}

/**
 * iOS can stop an idle service worker; a restarted worker has no sessions and
 * asks the page again. Only the walk that is open right now is ever answered.
 */
export function answerWorkerRequests(): void {
  navigator.serviceWorker?.addEventListener("message", (event: MessageEvent<WorkerToPage>) => {
    if (event.data?.type !== "walk-need") return;
    const port = event.ports[0];
    port?.postMessage(current && current.id === event.data.id ? current : null);
  });
}
